/**
 * Create's "N open" sheet: the player's OWN open challenges across every wager, grouped under a heading per wager,
 * smallest first. Blockari's `OwnWagersSheet` (c258375) with its `'all'` filter, which is the only one 21 Wild has:
 * the chips' "<N> yours" is plain caption text now (a chip is one tap target), and the way into the list is the teal
 * "N open" on Create's count line.
 *
 * What it lists: challenges the player MADE that are still going, whether waiting for a taker or being played (a
 * taker is playing the deck, or the player is, before it posts). A row being played says "Being played" in place of
 * its code. The rows come only from `GET /api/challenges/mine?open=1`, which the server scopes to the session player;
 * `ownOpenRows` is the second lock, not the first. The creator may see their own code, so a waiting row shows it,
 * whether it is public or private, when it was posted, and a link to it.
 */

import { myOpenChallenges, type ChallengeCard } from '@/net/api';
import { createLatestGate, loadClock } from '@/net/challenges-changed';
import { timeAgo } from '@/ui/game-row';
import { el } from '@/ui/hud';

/** A challenge of mine that is counted open but not waiting: a taker is playing it, or I am, before it posts. */
export const isBeingPlayed = (c: Pick<ChallengeCard, 'taken' | 'myScore'>): boolean => c.taken || c.myScore === null;

/**
 * The player's own open challenges, smallest wager first and oldest first among equals: made by me (`role` creator),
 * not resolved, and either waiting (not past its deadline) or being played. Anything else (a taken seat of mine, a
 * settled or expired challenge) is not "open".
 */
export function ownOpenRows(rows: readonly ChallengeCard[]): ChallengeCard[] {
  return rows
    .filter((c) => c.role === 'creator' && c.resolvedAt === null && !c.voided && (c.taken || !c.expired))
    .sort((a, b) => a.entryFee - b.entryFee || (a.createdAt < b.createdAt ? -1 : a.createdAt > b.createdAt ? 1 : 0));
}

/** Rows in order -> one run per wager (a heading opens each run of one amount). */
export function groupByWager(rows: readonly ChallengeCard[]): ChallengeCard[][] {
  const groups: ChallengeCard[][] = [];
  for (const c of rows) {
    const last = groups[groups.length - 1];
    if (last && last[0]!.entryFee === c.entryFee) last.push(c);
    else groups.push([c]);
  }
  return groups;
}

/**
 * The "N open" link on Create's count line: its accessible name ("View your 2 open challenges"), or null when it is
 * plain text (nothing open, or no way to open a list).
 */
export function openLinkLabel(open: number, canOpen: boolean): string | null {
  if (!canOpen || open <= 0) return null;
  return `View your ${open.toLocaleString('en-US')} open ${open === 1 ? 'challenge' : 'challenges'}`;
}

/** The count a group's heading reads: "3 open". */
export const groupOpenLabel = (n: number): string => `${n} open`;

const amount = (n: number): string => n.toLocaleString('en-US');

const ICON_X =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"></path></svg>';
const ICON_CHEVRON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6"></path></svg>';

/** One row of the list: the amount, when and public/private, the code (or "Being played"), and a link to the challenge. */
function ownRow(c: ChallengeCard, onOpen: (id: string) => void, now: number): HTMLElement {
  const li = el('li');
  const score = el('b', 'w21-pf-score', `${amount(c.entryFee)} `);
  score.append(el('small', undefined, '$CHAIN'));
  const when = el('span', 'w21-pf-when', `Posted ${timeAgo(c.createdAt, now)}`);
  when.append(el('span', `w21-pf-vis${c.isPrivate ? ' w21-priv' : ''}`, c.isPrivate ? 'Private' : 'Public'));
  li.append(score, when);
  if (isBeingPlayed(c)) li.append(el('span', 'w21-pf-playing', 'Being played'));
  else {
    const code = el('span', 'w21-pf-code', 'Code ');
    code.append(el('b', undefined, c.code ?? ''));
    li.append(code);
  }
  const link = el('a', 'w21-pf-link', 'View ') as HTMLAnchorElement;
  link.href = `#challenge/${c.id}`;
  link.setAttribute('aria-label', `View your ${amount(c.entryFee)} $CHAIN challenge`);
  link.insertAdjacentHTML('beforeend', ICON_CHEVRON);
  link.addEventListener('click', (event) => {
    event.preventDefault();
    onOpen(c.id);
  });
  li.append(link);
  return li;
}

/** The sheet's body for these rows (already `ownOpenRows`): a group per wager, or the empty line. */
export function ownWagerList(rows: readonly ChallengeCard[], onOpen: (id: string) => void, now = Date.now()): HTMLElement {
  const body = el('div');
  if (rows.length === 0) {
    const empty = el('div', 'w21-empty');
    empty.append(el('h3', undefined, 'Nothing open here'), el('p', undefined, 'You have nothing open right now.'));
    body.append(empty);
    return body;
  }
  for (const g of groupByWager(rows)) {
    const fee = amount(g[0]!.entryFee);
    const section = el('section', 'w21-pf-wager-group');
    const head = el('h3', 'w21-pf-wager-head', `${fee} `);
    head.append(el('small', undefined, '$CHAIN'), el('span', undefined, groupOpenLabel(g.length)));
    const list = el('ol', 'w21-pf-scores w21-pf-wagers');
    list.setAttribute('aria-label', `Your open ${fee} $CHAIN challenges`);
    list.append(...g.map((c) => ownRow(c, onOpen, now)));
    section.append(head, list);
    body.append(section);
  }
  return body;
}

let current: { layer: HTMLElement; onKey: (e: KeyboardEvent) => void; reload: () => void } | null = null;

/** Whether the sheet is up. */
export const ownWagersOpen = (): boolean => current !== null;

/** The list again from a newer read, in place (no "Loading…", scroll kept), if the sheet is up. */
export function refreshOwnWagers(): void {
  current?.reload();
}

export function closeOwnWagers(): void {
  if (!current) return;
  window.removeEventListener('keydown', current.onKey);
  window.removeEventListener('popstate', closeOwnWagers);
  window.removeEventListener('hashchange', closeOwnWagers);
  current.layer.remove();
  current = null;
}

/**
 * Open the sheet over Create: a bottom sheet on a phone, a centred card on a desktop, with a grab handle, a title and a
 * close button. Escape, the scrim and the close button dismiss it; the close button takes focus on open. `onOpen`
 * goes to a challenge (and the sheet goes with it).
 */
export function openOwnWagers(onOpen: (id: string) => void): void {
  closeOwnWagers();
  const layer = el('div', 'w21-pf-layer');
  const sheet = el('div', 'w21-pf-sheet');
  sheet.setAttribute('role', 'dialog');
  sheet.setAttribute('aria-modal', 'true');
  sheet.setAttribute('aria-labelledby', 'w21-pf-wagers-title');
  const grab = el('i', 'w21-pf-grab');
  grab.setAttribute('aria-hidden', 'true');
  const head = el('div', 'w21-pf-sheet-head');
  const title = el('h2', undefined, 'Your open challenges');
  title.id = 'w21-pf-wagers-title';
  const close = el('button', 'w21-icon-btn w21-sm') as HTMLButtonElement;
  close.type = 'button';
  close.setAttribute('aria-label', 'Close');
  close.innerHTML = ICON_X;
  close.addEventListener('click', closeOwnWagers);
  head.append(title, close);
  const body = el('div');
  const loading = el('p', 'w21-t-caption', 'Loading…');
  body.append(loading);
  sheet.append(grab, head, body);
  layer.append(sheet);
  layer.addEventListener('click', (event) => {
    if (event.target === layer) closeOwnWagers();
  });
  const onKey = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') closeOwnWagers();
  };
  window.addEventListener('keydown', onKey);
  // Going Back (or any route change) puts the sheet away with the screen it was over.
  window.addEventListener('popstate', closeOwnWagers);
  window.addEventListener('hashchange', closeOwnWagers);
  document.body.append(layer);
  // Latest wins: the first read and every refresh share one gate, so a slow older answer never lands over a newer one.
  const gate = createLatestGate();
  let shown = false;
  const load = (): void => {
    const latest = gate.begin();
    void myOpenChallenges().then((r) => {
      // The sheet may have been put away (or replaced by another open) while the list was on its way.
      if (current?.layer !== layer || !latest()) return;
      if (!r.ok) {
        // A failed re-read keeps the list that is showing.
        if (shown) return;
        const err = el('p', 'w21-t-caption', 'Could not load your challenges.');
        err.setAttribute('role', 'alert');
        body.replaceChildren(err);
        return;
      }
      const scroll = { sheet: sheet.scrollTop, layer: layer.scrollTop };
      const focused = document.activeElement instanceof HTMLAnchorElement && sheet.contains(document.activeElement)
        ? document.activeElement.getAttribute('href')
        : null;
      body.replaceChildren(
        ownWagerList(ownOpenRows(r.value), (id) => {
          closeOwnWagers();
          onOpen(id);
        }),
      );
      shown = true;
      loadClock.stamp('own-wagers');
      sheet.scrollTop = scroll.sheet;
      layer.scrollTop = scroll.layer;
      if (focused !== null) sheet.querySelector<HTMLElement>(`a[href="${CSS.escape(focused)}"]`)?.focus({ preventScroll: true });
    });
  };
  current = { layer, onKey, reload: load };
  close.focus();
  load();
}
